import { chatAxios, CHAT_API_BASE_URL, DISABLE_BACKEND_CHATBOT_CALLS } from './chatConfig';
import { mapConversationItem } from './chat/conversationMapper';

interface MessageMetadata {
  [key: string]: unknown;
}

interface ConversationContext {
  [key: string]: unknown;
}

export interface Message {
  id: string;
  content: string;
  direction: 'INCOMING' | 'OUTGOING';
  sender: 'CLIENT' | 'ADMIN' | 'BOT';
  timestamp?: string;
  createdAt: string;
  isRead: boolean;
  conversationId?: string;
  whatsappMessageId?: string | null;
  metadata?: MessageMetadata;
}

export interface Conversation {
  id: string;
  phoneNumber: string;
  clientName: string | null;
  state: 'BOT_ATIVO' | 'AGUARDANDO_HUMANO' | 'EM_ATENDIMENTO_HUMANO' | 'FINALIZADO';
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  adminId: string | null;
  assumedAt: string | null;
  context?: ConversationContext;
  messages?: Message[];
  clientPhone?: string;
  lastMessage?: string;
  unreadCount?: number;
}

export interface ConversationsResponse {
  data: Conversation[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const getHeaders = () => {
  return {
    'Content-Type': 'application/json',
  };
};

export class ChatAPIService {
  static async getConversations(
    page = 1,
    limit = 20,
    status?: string
  ): Promise<ConversationsResponse> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) {
      return { data: [] };
    }
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(status && { status }),
    });

    const response = await chatAxios.get(`${CHAT_API_BASE_URL}/conversations?${params}`, {
      headers: getHeaders(),
    });

    const conversations = Array.isArray(response.data) ? response.data : response.data.data || [];
    const normalized: Conversation[] = conversations.map(mapConversationItem);

    return {
      data: normalized,
      pagination: response.data.pagination,
    };
  }

  static async getConversation(id: string): Promise<Conversation> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) {
      throw new Error('Chat temporariamente desativado');
    }
    const response = await chatAxios.get(`${CHAT_API_BASE_URL}/conversations/${id}`, {
      headers: getHeaders(),
    });

    return mapConversationItem(response.data);
  }

  static async getMessages(
    conversationId: string,
    page = 1,
    limit = 50
  ): Promise<{ data: Message[] }> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) {
      return { data: [] };
    }
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await chatAxios.get(
      `${CHAT_API_BASE_URL}/conversations/${conversationId}/messages?${params}`,
      { headers: getHeaders() }
    );
    return response.data;
  }

  static async getWaitingConversations(): Promise<Conversation[]> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) {
      return [];
    }
    const response = await chatAxios.get(`${CHAT_API_BASE_URL}/waiting`, { headers: getHeaders() });
    return response.data;
  }

  static async getWaitingCount(): Promise<number> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) {
      return 0;
    }
    const response = await chatAxios.get(`${CHAT_API_BASE_URL}/waiting-count`, {
      headers: getHeaders(),
    });
    return response.data.count;
  }

  static async assumeConversation(
    conversationId: string,
    adminId?: string,
    adminName?: string
  ): Promise<void> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) return;
    await chatAxios.post(
      `${CHAT_API_BASE_URL}/conversations/${conversationId}/assume`,
      { adminId, adminName },
      { headers: getHeaders() }
    );
  }

  static async returnToBot(conversationId: string): Promise<void> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) return;
    await chatAxios.post(
      `${CHAT_API_BASE_URL}/conversations/${conversationId}/return-to-bot`,
      {},
      { headers: getHeaders() }
    );
  }

  static async sendMessage(
    conversationId: string,
    message: string,
    adminId?: string,
    adminName?: string
  ): Promise<void> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) return;
    await chatAxios.post(
      `${CHAT_API_BASE_URL}/send`,
      { conversationId, message, adminId, adminName },
      { headers: getHeaders() }
    );
  }

  static async markAsRead(conversationId: string, messageIds?: string[]): Promise<void> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) return;
    await chatAxios.put(
      `${CHAT_API_BASE_URL}/conversations/${conversationId}/mark-read`,
      {},
      { headers: getHeaders() }
    );
  }

  static async updateConversationState(
    conversationId: string,
    state: Conversation['state']
  ): Promise<void> {
    if (DISABLE_BACKEND_CHATBOT_CALLS) return;
    await chatAxios.patch(
      `${CHAT_API_BASE_URL}/conversations/${conversationId}/state`,
      { state },
      { headers: getHeaders() }
    );
  }
}

export { ChatAuthService } from './chatAuthService';
